import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Save, Clock } from "lucide-react";
import { api } from "@/lib/api";

const inputCls = "w-full bg-[#0D0D0C] border border-[#F4F0EB]/10 rounded-lg px-4 py-3 font-sub text-sm text-[#F4F0EB] placeholder:text-[#6E695E] focus:border-[#C88D51]/60 focus:outline-none transition-colors";
const emptySvc = { name: "", price_from: 0, duration: "", description: "", featuresText: "" };

export const ServicesManager = () => {
  const [services, setServices] = useState([]);
  const [slots, setSlots] = useState("");
  const [newSvc, setNewSvc] = useState(emptySvc);
  const [saving, setSaving] = useState("");

  const load = async () => {
    const [s, sl] = await Promise.all([api.get("/services"), api.get("/slots")]);
    setServices(s.data.map((x) => ({ ...x, featuresText: (x.features || []).join("\n") })));
    setSlots(sl.data.slots.join(", "));
  };
  useEffect(() => { load(); }, []);

  const upd = (i, k, v) => setServices((prev) => prev.map((s, j) => (j === i ? { ...s, [k]: v } : s)));

  const save = async (s) => {
    setSaving(s.slug);
    try {
      await api.patch(`/services/${s.slug}`, {
        name: s.name, price_from: parseInt(s.price_from, 10) || 0, duration: s.duration,
        description: s.description, features: s.featuresText.split("\n").map((f) => f.trim()).filter(Boolean),
      });
      toast.success("Prestation mise à jour ✦");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setSaving("");
  };

  const create = async () => {
    if (!newSvc.name || !newSvc.price_from) { toast.error("Nom et tarif requis."); return; }
    setSaving("new");
    try {
      await api.post("/services", {
        name: newSvc.name, price_from: parseInt(newSvc.price_from, 10) || 0, duration: newSvc.duration,
        description: newSvc.description, features: newSvc.featuresText.split("\n").map((f) => f.trim()).filter(Boolean),
      });
      toast.success("Prestation créée ✦");
      setNewSvc(emptySvc);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setSaving("");
  };

  const remove = async (slug) => { await api.delete(`/services/${slug}`); toast.success("Prestation supprimée"); load(); };

  const saveSlots = async () => {
    setSaving("slots");
    try {
      const { data } = await api.put("/settings/slots", { slots: slots.split(",").map((s) => s.trim()).filter(Boolean) });
      setSlots(data.slots.join(", "));
      toast.success("Créneaux mis à jour ✦");
    } catch (e) { toast.error(e.response?.data?.detail || "Format attendu : HH:MM, HH:MM…"); }
    setSaving("");
  };

  const fields = (s, set) => (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <input placeholder="Nom" value={s.name} onChange={(e) => set("name", e.target.value)} className={inputCls + " sm:col-span-2"} />
        <input type="number" placeholder="Tarif dès (€)" value={s.price_from} onChange={(e) => set("price_from", e.target.value)} className={inputCls} />
      </div>
      <input placeholder="Durée (ex : Demi-journée)" value={s.duration} onChange={(e) => set("duration", e.target.value)} className={inputCls} />
      <textarea placeholder="Description" rows={2} value={s.description} onChange={(e) => set("description", e.target.value)} className={inputCls} />
      <textarea placeholder="Points inclus (un par ligne)" rows={3} value={s.featuresText} onChange={(e) => set("featuresText", e.target.value)} className={inputCls} />
    </div>
  );

  return (
    <div className="space-y-8">
      <div className="bg-[#161513] border border-[#F4F0EB]/8 rounded-xl p-6">
        <h3 className="font-serif-d text-xl mb-1 flex items-center gap-2"><Clock className="w-4 h-4 text-[#C88D51]" /> Créneaux de réservation</h3>
        <p className="font-sub text-xs text-[#6E695E] mb-4">Horaires proposés chaque jour, séparés par des virgules (format HH:MM).</p>
        <div className="flex gap-3">
          <input data-testid="admin-slots-input" value={slots} onChange={(e) => setSlots(e.target.value)} className={inputCls} />
          <button data-testid="admin-slots-save" onClick={saveSlots} disabled={saving === "slots"} className="btn-accent px-6 py-3 font-sub text-sm flex items-center gap-2 disabled:opacity-60 shrink-0">
            {saving === "slots" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {services.map((s, i) => (
          <div key={s.slug} data-testid={`admin-service-card-${s.slug}`} className="bg-[#161513] border border-[#F4F0EB]/8 rounded-xl p-6">
            <div className="font-mono-a text-[0.6rem] uppercase tracking-[0.2em] text-[#6E695E] mb-4">{s.slug}</div>
            {fields(s, (k, v) => upd(i, k, v))}
            <div className="flex gap-2 mt-4">
              <button data-testid={`admin-service-save-${s.slug}`} onClick={() => save(s)} disabled={saving === s.slug} className="btn-accent px-5 py-2.5 font-sub text-xs flex items-center gap-2 disabled:opacity-60">
                {saving === s.slug ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Enregistrer
              </button>
              <button data-testid={`admin-service-delete-${s.slug}`} onClick={() => remove(s.slug)} className="btn-ghost px-4 py-2.5 font-sub text-xs flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Supprimer</button>
            </div>
          </div>
        ))}

        <div className="bg-[#161513] border border-dashed border-[#F4F0EB]/15 rounded-xl p-6">
          <h3 className="font-serif-d text-xl mb-4">Nouvelle prestation</h3>
          {fields(newSvc, (k, v) => setNewSvc({ ...newSvc, [k]: v }))}
          <button data-testid="admin-service-create" onClick={create} disabled={saving === "new"} className="btn-accent w-full mt-4 py-3 font-sub text-sm flex items-center justify-center gap-2 disabled:opacity-60">
            {saving === "new" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Créer la prestation
          </button>
        </div>
      </div>
    </div>
  );
};
